import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { paymentService } from '../../services/paymentService';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import './BankDetails.css';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const BankDetails = () => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    account_holder_type: 'individual',
    account_number: '',
    sort_code: '',
  });
  const [errors, setErrors] = useState({});

  // Fetch existing bank details
  const { data: bankDetailsResponse, isLoading, error } = useQuery({
    queryKey: ['bankDetails'],
    queryFn: () => paymentService.getBankDetails(),
    retry: false, // Don't retry on 404
    onError: (err) => {
      if (err.status !== 404) {
        showErrorToast('Error loading bank details');
      }
    },
    select: (data) => {
      // Extract first item from list (users should only have one bank detail)
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    },
  });

  const bankDetails = bankDetailsResponse;

  // Update form data when bank details are loaded
  useEffect(() => {
    if (bankDetails) {
      setIsEditing(false);
      // Note: We don't have account_number and sort_code from API (security)
      // These fields are for input only
    }
  }, [bankDetails]);

  // Create/Update mutation
  const mutation = useMutation({
    mutationFn: async (data) => {
      if (bankDetails?.id) {
        return paymentService.patchBankDetails(bankDetails.id, data);
      } else {
        return paymentService.createBankDetails(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['bankDetails']);
      setIsEditing(false);
      setFormData({
        account_holder_name: '',
        account_holder_type: 'individual',
        account_number: '',
        sort_code: '',
      });
      showSuccessToast(bankDetails?.id ? 'Bank details updated successfully' : 'Bank details added successfully');
    },
    onError: (error) => {
      const errorMessage = error.data?.error || error.data?.detail || error.message || 'Failed to save bank details';
      showErrorToast(errorMessage);
      setErrors(error.data || {});
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const formatSortCode = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XX-XX-XX
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
  };

  const handleSortCodeChange = (e) => {
    const formatted = formatSortCode(e.target.value);
    setFormData(prev => ({
      ...prev,
      sort_code: formatted
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.account_holder_name.trim()) {
      newErrors.account_holder_name = 'Account holder name is required';
    }
    
    if (!formData.account_number.trim()) {
      newErrors.account_number = 'Account number is required';
    } else if (!/^\d{8}$/.test(formData.account_number.replace(/\s/g, ''))) {
      newErrors.account_number = 'Account number must be 8 digits';
    }
    
    if (!formData.sort_code.trim()) {
      newErrors.sort_code = 'Sort code is required';
    } else if (!/^\d{2}-\d{2}-\d{2}$/.test(formData.sort_code)) {
      newErrors.sort_code = 'Sort code must be in format XX-XX-XX';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsCreatingToken(true);
    setErrors({});

    try {
      if (!stripePromise) {
        throw new Error('Stripe publishable key is missing. Set REACT_APP_STRIPE_PUBLISHABLE_KEY.');
      }

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe is not initialized. Please check your Stripe publishable key.');
      }

      // Create bank account token using Stripe.js
      const result = await stripe.createToken('bank_account', {
        country: 'GB',
        currency: 'gbp',
        account_number: formData.account_number.replace(/\s/g, ''),
        routing_number: formData.sort_code.replace(/-/g, ''), // For UK, this is sort code
        account_holder_name: formData.account_holder_name,
        account_holder_type: formData.account_holder_type,
      });

      if (result.error) {
        const errorMessage = result.error.message || 'Failed to create bank account token';
        showErrorToast(errorMessage);
        setIsCreatingToken(false);
        return;
      }

      // Send token to backend
      const token = result.token;
      mutation.mutate({
        stripe_bank_token: token.id,
        account_holder_name: formData.account_holder_name,
        account_holder_type: formData.account_holder_type,
      });
    } catch (error) {
      console.error('Error creating bank token:', error);
      showErrorToast(error.message || 'Failed to process bank details');
    } finally {
      setIsCreatingToken(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      account_holder_name: '',
      account_holder_type: 'individual',
      account_number: '',
      sort_code: '',
    });
    setErrors({});
  };

  const handleDelete = async () => {
    if (!bankDetails?.id) return;
    
    if (!window.confirm('Are you sure you want to delete your bank details?')) {
      return;
    }

    try {
      await paymentService.deleteBankDetails(bankDetails.id);
      queryClient.invalidateQueries(['bankDetails']);
      showSuccessToast('Bank details deleted successfully');
    } catch (error) {
      const errorMessage = error.data?.error || error.data?.detail || error.message || 'Failed to delete bank details';
      showErrorToast(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="bank-details">
        <div className="bank-details__loading">Loading bank details...</div>
      </div>
    );
  }

  const hasBankDetails = bankDetails && !error;

  return (
    <div className="bank-details">
      <div className="bank-details__header">
        <h2 className="bank-details__title">Bank Details</h2>
        {!isEditing && hasBankDetails && (
          <div className="bank-details__header-actions">
            <button
              className="bank-details__edit-button"
              onClick={() => setIsEditing(true)}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit
            </button>
          </div>
        )}
      </div>

      {hasBankDetails && bankDetails.is_verified && (
        <div className="bank-details__verified-badge">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Verified
        </div>
      )}

      {!hasBankDetails && !isEditing && (
        <div className="bank-details__empty">
          <p>No bank details found. Add your bank details to receive payments and manage payment requests.</p>
          <button
            className="bank-details__add-button"
            onClick={() => setIsEditing(true)}
          >
            Add Bank Details
          </button>
        </div>
      )}

      {isEditing && (
        <form onSubmit={handleSave} className="bank-details__form">
          <div className="bank-details__content">
            <div className="bank-details__field">
              <label className="bank-details__label">
                Account Holder Name <span className="bank-details__required">*</span>
              </label>
              <input
                type="text"
                name="account_holder_name"
                value={formData.account_holder_name}
                onChange={handleChange}
                className={`bank-details__input ${errors.account_holder_name ? 'bank-details__input--error' : ''}`}
                placeholder="John Doe"
                required
              />
              {errors.account_holder_name && (
                <span className="bank-details__error-text">{errors.account_holder_name}</span>
              )}
            </div>

            <div className="bank-details__field">
              <label className="bank-details__label">
                Account Holder Type <span className="bank-details__required">*</span>
              </label>
              <select
                name="account_holder_type"
                value={formData.account_holder_type}
                onChange={handleChange}
                className="bank-details__input"
              >
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </div>

            <div className="bank-details__field">
              <label className="bank-details__label">
                Account Number <span className="bank-details__required">*</span>
              </label>
              <input
                type="text"
                name="account_number"
                value={formData.account_number}
                onChange={handleChange}
                className={`bank-details__input ${errors.account_number ? 'bank-details__input--error' : ''}`}
                placeholder="12345678"
                maxLength="8"
                pattern="[0-9]{8}"
                required
              />
              {errors.account_number && (
                <span className="bank-details__error-text">{errors.account_number}</span>
              )}
            </div>

            <div className="bank-details__field">
              <label className="bank-details__label">
                Sort Code <span className="bank-details__required">*</span>
              </label>
              <input
                type="text"
                name="sort_code"
                value={formData.sort_code}
                onChange={handleSortCodeChange}
                className={`bank-details__input ${errors.sort_code ? 'bank-details__input--error' : ''}`}
                placeholder="12-34-56"
                maxLength="8"
                required
              />
              {errors.sort_code && (
                <span className="bank-details__error-text">{errors.sort_code}</span>
              )}
            </div>

            <div className="bank-details__security-note">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Your bank details are securely processed by Stripe. We never store your full account details.
            </div>

            <div className="bank-details__actions">
              <button
                type="button"
                className="bank-details__button bank-details__button--cancel"
                onClick={handleCancel}
                disabled={isCreatingToken || mutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bank-details__button bank-details__button--save"
                disabled={isCreatingToken || mutation.isPending}
              >
                {isCreatingToken ? 'Processing...' : mutation.isPending ? 'Saving...' : 'Save Bank Details'}
              </button>
            </div>
          </div>
        </form>
      )}

      {hasBankDetails && !isEditing && (
        <div className="bank-details__display">
          <div className="bank-details__content">
            <div className="bank-details__field">
              <label className="bank-details__label">Account Holder Name</label>
              <p className="bank-details__value">{bankDetails.account_holder_name}</p>
            </div>

            <div className="bank-details__field">
              <label className="bank-details__label">Account Holder Type</label>
              <p className="bank-details__value">
                {bankDetails.account_holder_type === 'individual' ? 'Individual' : 'Company'}
              </p>
            </div>

            <div className="bank-details__field">
              <label className="bank-details__label">Bank Account</label>
              <p className="bank-details__value">
                {bankDetails.masked_account_info?.message || 'Securely stored with Stripe'}
              </p>
            </div>

            <div className="bank-details__actions">
              <button
                className="bank-details__button bank-details__button--delete"
                onClick={handleDelete}
              >
                Delete Bank Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankDetails;
